<template>
  <div class="chometz-attachments-view" data-cy="chometz-attachments-view">
    <div v-if="attachments.length" class="chometz-attachments-view__container">
      <div
        class="chometz-attachments-view__header"
        data-cy="chometz-attachments-header"
      >
        Uploaded Documents
      </div>
      <div class="chometz-attachments-view__attachments">
        <div
          v-for="attachment in attachments"
          :key="attachment.Name"
          class="chometz-attachments-view__attachment"
          data-cy="ingredient-attachment"
        >
          <LoadingButton
            :loading="downloadingAttachment === attachment"
            class="chometz-attachments-view__attachment__download"
            type="button"
            theme-color="primary"
            @click="downloadAttachmentAsync(attachment)"
            data-cy="download-attachment-button"
            :disabled="
              downloadingAttachment != null &&
              downloadingAttachment !== attachment
            "
          >
            {{ attachment.Name }}{{ attachment.DocType }}
          </LoadingButton>
          <div
            class="chometz-attachments-view__attachment__date"
            data-cy="attachment-date"
          >
            {{
              attachment.Created &&
              new Date(attachment.Created).toLocaleString()
            }}
          </div>
          <div
            v-if="allowDelete"
            class="chometz-attachments-view__attachment__actions"
          >
            <Button
              type="button"
              theme-color="error"
              @click="deleteAttachmentAsync(attachment)"
              data-cy="delete-attachment-button"
            >
              Delete
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { onBeforeMount, ref, watch } from "vue";
import { Button } from "@progress/kendo-vue-buttons";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { useNotificationStore } from "@/stores/useNotificationStore";
import LoadingButton from "@/shared/ui/inputs/LoadingButton.vue";
import type { Attachment } from "@/Domain/Attachment";

const notificationStore = useNotificationStore();

const props = defineProps<{
  companyId?: number;
  plantId?: number;
  saleYear?: number;
  allowDelete?: boolean;
}>();

const attachments = ref<Attachment[]>([]);
const downloadingAttachment = ref<Attachment | null>(null);

const updateAttachmentsAsync = async function () {
  attachments.value = [];
  return handleResponsePromiseAsync(
    api.get("GetChometzAttachments", {
      companyId: props.companyId ?? 0,
      plantId: props.plantId ?? 0,
      saleYear: props.saleYear ?? 0,
    }),
    (data) => {
      attachments.value = data;
    },
    "ChometzAttachmentsView.vue",
    "updateAttachmentsAsync"
  );
};

const downloadAttachmentAsync = async function (attachment: Attachment) {
  try {
    downloadingAttachment.value = attachment;

    const downloadPromise = api.getDownload("DownloadChometzAttachment", {
      companyId: props.companyId ?? 0,
      plantId: props.plantId ?? 0,
      saleYear: props.saleYear ?? 0,
      fileName: attachment.Name + attachment.DocType,
    });

    await handleResponsePromiseAsync(
      downloadPromise,
      () => {},
      "ChometzAttachmentsView.vue",
      "downloadAttachmentAsync",
      () => {
        notificationStore.notify({
          type: NotificationTypeEnum.error,
          message: `Error downloading ${attachment.Name}`,
          duration: 5000,
        });
      }
    );
  } finally {
    downloadingAttachment.value = null;
  }
};

const deleteAttachmentAsync = async function (attachment: Attachment) {
  await handleResponsePromiseAsync(
    api.post("DeleteChometzAttachment", {
      CompanyId: props.companyId ?? 0,
      PlantId: props.plantId ?? 0,
      FileName: attachment.Name + attachment.DocType,
    }),
    (data) => {
      if (data)
        attachments.value.splice(attachments.value.indexOf(attachment), 1);
    },
    "ChometzAttachmentsView.vue",
    "deleteAttachmentAsync",
    () => {
      notificationStore.notify({
        type: NotificationTypeEnum.error,
        message: `Error deleting ${attachment.Name}`,
        duration: 5000,
      });
    }
  );
};

defineExpose({
  updateAttachmentsAsync,
});

watch(
  () => props.plantId,
  async () => {
    await updateAttachmentsAsync();
  }
);

onBeforeMount(async () => {
  await updateAttachmentsAsync();
});
</script>

<style lang="scss">
.chometz-attachments-view {
  display: flex;
  flex-direction: column;
  width: 100%;

  &__header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    font-size: 14px;
    font-weight: bolder;
  }

  &__attachments {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    overflow-y: auto;
  }

  &__attachment {
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    align-items: center;
    margin-bottom: 1rem;
    padding: 0.5rem 1rem;
    border: 1px solid #ccc;
    border-radius: 0.5rem;
    flex-wrap: wrap;
    gap: 4px;

    &__download {
      flex-grow: 3;
    }

    &__date {
      font-weight: bold;
      text-align: center;
      flex-basis: 190px;
    }

    &__actions {
      flex-basis: 60px;
      display: flex;
      justify-content: flex-end;
    }
  }
}
</style>
